/**
 * The page is going away (reload, tab close, navigation off the app) or out of
 * sight (tab switch, app switch on a phone, which a phone may follow with a
 * kill and no further event). A debounced write still waiting at this point
 * is lost unless it is sent now, and a plain fetch sent from `pagehide` is
 * cancelled with the page — so a write sent here asks for `keepalive`.
 *
 * Unmount effects do not run when a page unloads, so a debounce's own
 * "flush on unmount" never covers this case.
 */

let leaving = false;

/** True while the page is hidden or unloading: a write sent now must outlive
 *  the page (`fetch` with `keepalive`). */
export function isPageLeaving(): boolean {
  return leaving || (typeof document !== "undefined" && document.visibilityState === "hidden");
}

// Browsers refuse a `keepalive` request whose body passes 64 KiB (the fetch
// rejects outright), so a larger write is sent as a plain fetch instead. The
// margin leaves room for the operation text around the variables.
const KEEPALIVE_VARIABLES_MAX_BYTES = 56 * 1024;

/** The Apollo mutation context for a write that may be sent as the page
 *  leaves: `keepalive` then, nothing otherwise. Pass the mutation's
 *  variables so a body too large for `keepalive` is not sent with it. */
export function pageLeaveContext(
  variables?: unknown,
): { fetchOptions?: { keepalive: true } } {
  if (!isPageLeaving()) return {};
  if (
    variables !== undefined &&
    new TextEncoder().encode(JSON.stringify(variables)).length > KEEPALIVE_VARIABLES_MAX_BYTES
  ) {
    return {};
  }
  return { fetchOptions: { keepalive: true } };
}

const flushes = { first: new Set<() => void>(), last: new Set<() => void>() };
let listening = false;

function runFlushes() {
  for (const flush of [...flushes.first, ...flushes.last]) flush();
}

function listen() {
  if (listening || typeof window === "undefined") return;
  listening = true;
  window.addEventListener("pagehide", () => {
    leaving = true;
    runFlushes();
  });
  // A page restored from the back/forward cache is live again.
  window.addEventListener("pageshow", () => {
    leaving = false;
  });
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "hidden") runFlushes();
  });
}

/**
 * Runs `flush` when the page is hidden or unloads.
 *
 * `order` matters where one flush feeds another: an editor's debounce hands
 * its text to a store whose own debounce must then send it. The editor
 * flushes `first` and the store `last`, whichever subscribed first — so one
 * listener runs them all, in that order, rather than one listener each.
 */
export function onPageLeave(flush: () => void, order: "first" | "last" = "first"): () => void {
  listen();
  // A wrapper per subscription, so the same function subscribed twice
  // unsubscribes once.
  const entry = () => flush();
  flushes[order].add(entry);
  return () => {
    flushes[order].delete(entry);
  };
}
